import { Box, styled, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ExternalLink from '@mui/material/Link'
import ViewTitle from '../../components/ViewTitle'

export const Header = styled(Box)(() => ({
  background: 'url("//content.photobucket.com/img/homepage/secondary-header-bg.webp") center center no-repeat',
  backgroundSize: 'cover',
  height: '300px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
})) as typeof Box

const ContentWrapper = styled(Box)(({ theme }) => ({
  padding: '0 20px',
  margin: '20px 0 0',
  [theme.breakpoints.up('md')]: {
    margin: '100px 0 0',
    padding: '0 140px'
  }
})) as typeof Box

export const Subhead = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  lineHeight: 1.6,
  textAlign: 'left',
  marginBottom: '10px',
  [theme.breakpoints.up('md')]: {
    fontSize: '2.25rem',
    marginBottom: '20px'
  }
}))

const ContentText = styled(Typography)(({ theme }) => ({
  fontFamily: '"proxima-nova", sans-serif',
  fontSize: '1rem',
  lineHeight: 1.5,
  margin: '10px 0 20px',
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.375rem',
    margin: '20px 0 40px'
  }
}))

const ReferenceLink = styled(ExternalLink)(() => ({
  textDecoration: 'underline',
  fontWeight: 600,
  color: '#1C47CB'
})) as typeof ExternalLink

export default function AccessibilityCommitment() {
  const theme = useTheme()

  return (
    <Box component="main" sx={{ maxWidth: theme.breakpoints.values.xl, width: '100%', margin: '0 auto' }}>
      <ViewTitle title="Accessibility Assistance" />
      <Header>
        <Typography
          variant="h1"
          id="mainTitle"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.75rem' },
            color: '#fff',
            textAlign: 'center'
          }}
        >
          Photobucket Accessibility Statement
        </Typography>
      </Header>
      <ContentWrapper component="article">
        <Subhead variant="h2">Accessibility Assistance</Subhead>
        <ContentText>
          If you have difficulty using or accessing any element of the Photobucket website or mobile app, please feel free to contact{' '}
          <ReferenceLink
            href="https://zendesk.photobucket.com/login/?return_to=https%3A%2F%2Fsupport.photobucket.com%2Fhc%2Fen-us%2Frequests%2Fnew"
            target="_blank"
          >
            photobucket support.
          </ReferenceLink>{' '}
          We will work with you to provide the information, item, or transaction you seek through a communication method that is accessible for you
          consistent with applicable law (for example, through telephone support).
        </ContentText>
        <Subhead variant="h2">A Goal of Being Better</Subhead>
        <ContentText>
          Photobucket desires to provide a positive customer experience to all our customers, and we aim to promote accessibility and inclusion. Our
          goal is to permit our customers to successfully store their photos and transact business through our website. Whether you are using
          assistive technologies like a screen reader or a magnifier, our goal is to make your use of Photobucket’s website a successful and enjoyable
          experience.
        </ContentText>
        <Subhead variant="h2">Actions</Subhead>
        <ContentText>
          We are taking a variety of steps and devoting resources to further enhance the accessibility of our website and become fully{' '}
          <ReferenceLink href="https://www.w3.org/TR/WCAG20/" target="_blank">
            WCAG 2.0
          </ReferenceLink>{' '}
          compliant by the end of 2022.
        </ContentText>
        <Subhead variant="h2">An Ongoing Effort</Subhead>
        <ContentText>
          Although we are proud of the efforts that we have completed and those that are in progress, we at Photobucket view accessibility as an
          ongoing effort. We are already looking down the road at the{' '}
          <ReferenceLink href="https://www.w3.org/WAI/standards-guidelines/wcag/wcag3-intro/" target="_blank">
            {' '}
            WCAG 3.0 draft
          </ReferenceLink>{' '}
        </ContentText>
        <Subhead variant="h2">Feedback</Subhead>
        <ContentText>
          Please contact{' '}
          <ReferenceLink
            href="https://zendesk.photobucket.com/login/?return_to=https%3A%2F%2Fsupport.photobucket.com%2Fhc%2Fen-us%2Frequests%2Fnew"
            target="_blank"
          >
            photobucket support
          </ReferenceLink>{' '}
          if you have any feedback or suggestions as to how we could improve the accessibility of this website.
        </ContentText>
      </ContentWrapper>
    </Box>
  )
}
